import type { LC, LiveElement } from '@use-gpu/live';
import type { StorageSource, TextureSource, TypedArray } from '@use-gpu/core';

import { memo, yeet, useRef, useResource } from '@use-gpu/live';

import { useReadbackStorage } from '../hooks/useReadbackStorage';
import { useReadbackTexture } from '../hooks/useReadbackTexture';

export type ReadbackProps = {
  source?: StorageSource | TextureSource,
  sources?: (StorageSource | TextureSource)[],
  then?: (...data: TypedArray[]) => LiveElement,

  buffers?: number,
  once?: boolean,

  shouldDispatch?: () => boolean | number | undefined,
  onDispatch?: () => void,
};

export const Readback: LC<ReadbackProps> = memo((props: ReadbackProps) => {
  const {
    source,
    sources,
    then,
    buffers,
    once,
    shouldDispatch,
    onDispatch,
  } = props;

  let dispatchVersion: number | null = null;
  let dispatched = false;

  let cancelled = false;
  useResource((dispose) => dispose(() => cancelled = true));

  let read = false;
  const lastRender = useRef<LiveElement>(null);

  const resolvedSources = sources ? sources : source ? [source] : [];
  const readbacks = resolvedSources.map(source =>
    (source as any).texture ? useReadbackTexture(source as TextureSource, buffers) : useReadbackStorage(source as StorageSource, buffers)
  );

  return yeet({
    post: () => {
      if (cancelled) return null;
      dispatched = false;

      if (once && read) return null;

      if (shouldDispatch) {
        const d = shouldDispatch();
        if (d === false) return null;
        if (typeof d === 'number') {
          if (dispatchVersion === d) return null;
          dispatchVersion = d;
        }
      }
      onDispatch?.();
      dispatched = true;

      return readbacks.map(r => r.dispatchCopy()).filter(r => !!r);
    },
    readback: async () => {
      if (cancelled) return null;
      if (!dispatched) return lastRender.current;

      read = true;
      const data = await Promise.all(readbacks.map(r => r.asyncRead()));

      const hasNull = !data || data.some(d => d == null);
      if (hasNull) return lastRender.current;

      return then ? (lastRender.current = then(...data as TypedArray[])) : null;
    },
  });
}, 'Readback');
